import { StepLoading } from "@/base/wizard/StepLoading";
import { NumericField } from "@/components/NumericField";
import { SearchSelect } from "@/components/SearchSelect";
import { SelectField } from "@/components/SelectField";
import { TextField } from "@/components/TextField";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import { ToggleSection, YesNo, type WizardFieldErrors } from "@/base";
import { showWhenConditions, type ManifestField } from "@/lib/brokerProducts";
import {
  asBoolean,
  asNumber,
  defaultHint,
  isAsked,
  type WcAnswer,
  type WcAnswers,
} from "@/lib/submissions/wc/questions";
import type { usePriorCarriers } from "@/lib/usePriorCarriers";

/** Asked of the experience mod, whose default (1.00) means "no mod". */
const NO_MOD_HINT = "Enter 1.00 if the insured has no mod.";

const asString = (v: WcAnswer) => (typeof v === "string" ? v : "");

/**
 * One manifest field, by its declared type (D-10 rule 1), its default only a
 * hint: a Questions and coverage answer (BRK-S304), or one of Basic
 * information's (BRK-S388). A prior carrier picks from the carriers its page
 * reads.
 */
export function QuestionField({
  field: f,
  value,
  onChange,
  errors,
  busy,
  carriers,
}: {
  field: ManifestField;
  value: WcAnswer;
  onChange: (value: WcAnswer) => void;
  errors: WizardFieldErrors;
  busy: boolean;
  carriers: ReturnType<typeof usePriorCarriers>;
}) {
  const hint = defaultHint(f);
  switch (f.type) {
    case "currency":
    case "number":
      if (f.integer)
        return (
          <TextField
            label={f.unit ? `${f.label} (${f.unit})` : f.label}
            type="number"
            step={1}
            min={f.min}
            max={f.max}
            value={asNumber(value) ?? ""}
            onChange={(e) =>
              onChange(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
            error={errors[f.key]}
            disabled={busy}
            required={f.required !== false}
          />
        );
      // Non-integer numbers retain the shared numeric formatting.
      return (
        <div className="flex flex-col gap-1 sm:max-w-xs">
          <NumericField
            kind={f.type}
            label={f.label}
            value={asNumber(value)}
            onChange={onChange}
            error={errors[f.key]}
            min={f.min}
            max={f.max}
            placeholder={hint}
            required={f.required !== false}
          />
          {f.key === "experienceMod" && (
            <p className="text-xs text-brk-dim">{NO_MOD_HINT}</p>
          )}
        </div>
      );
    case "priorCarrier":
      if (carriers.problem)
        return (
          <StepLoadProblem
            problem={carriers.problem}
            onRetry={carriers.retry}
          />
        );
      if (carriers.items === null)
        return <StepLoading label="Loading current carriers…" framed={false} />;
      // A long list, so it is searchable (BRK-S314).
      return (
        <SearchSelect
          label={f.label}
          options={carriers.items.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="Choose one"
          value={asString(value)}
          onChange={(id) => onChange(id || undefined)}
          error={errors[f.key]}
          disabled={busy}
          required={f.required !== false}
        />
      );
    case "select":
      return (
        <div className="sm:max-w-xs">
          <SelectField
            label={f.label}
            options={f.options ?? []}
            placeholder={hint ? `Choose (${hint})` : "Choose one"}
            value={asString(value)}
            onChange={(e) => onChange(e.target.value || undefined)}
            error={errors[f.key]}
            disabled={busy}
            required={f.required !== false}
          />
        </div>
      );
    case "boolean":
      // A question the line asks outright, or an ACORD one (its form wording
      // is its help), is a Yes/No; any other opens a section of its own.
      if (f.required !== undefined || f.helpText !== undefined)
        return (
          <YesNo
            label={f.label}
            help={f.helpText}
            value={asBoolean(value)}
            onChange={onChange}
            error={errors[f.key]}
            disabled={busy}
          />
        );
      return (
        <ToggleSection
          question={f.label.endsWith("?") ? f.label : `${f.label}?`}
          value={asBoolean(value)}
          onChange={onChange}
          clearPrompt=""
          error={errors[f.key]}
        >
          <p className="text-body text-brk-dim">
            It will be asked for on the policy.
          </p>
        </ToggleSection>
      );
    default:
      return null;
  }
}

/** PMS's refusal of an answer to a question it no longer asks (PMS-S762),
 *  shown under the question whose answer hid it: the hidden field has no
 *  control of its own to carry it. */
export function HiddenAnswerErrors({
  question,
  fields,
  answers,
  errors,
}: {
  question: ManifestField;
  fields: readonly ManifestField[];
  /** The answers as asked now (`shownAnswers`). */
  answers: WcAnswers;
  errors: WizardFieldErrors;
}) {
  return fields
    .filter(
      (hidden) =>
        showWhenConditions(hidden.showWhen).some(
          (c) => c.key === question.key,
        ) &&
        !isAsked(hidden, answers) &&
        errors[hidden.key],
    )
    .map((hidden) => (
      <p key={hidden.key} role="alert" className="text-sm text-brk-danger">
        {errors[hidden.key]}
      </p>
    ));
}
